import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { formatThousandAndUnit } from '@/utils/utils';
import {
  apiV2WaterMapDataCardDataPost,
  apiV2WaterMapDataFlowmeterDataPost,
  V2ElectricMapDataAreaPostResponse,
  V2ElectricMapDataAreaV2PostResponse,
  V2WaterMapDataCardDataPostResponse,
  V2WaterMapDataFlowmeterDataPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import type {
  RenderStreamingVideoElement,
  UnityCall,
  UnityCameraData,
  UnityClickDetail,
} from '../../../UnityRenderMap';
import {
  clearUnityBusinessCleanupPayloads,
  setUnityBusinessCleanupPayloads,
} from '../../../UnityRenderMap/businessCleanup';
import { getUnityPosition, notEmpty } from '../../../UnityRenderMap/utils';
import { UnityOverlay, useSharedUnityMap } from '../../../SharedUnityMapLayer';

interface MapViewProps {
  className?: string;
  children?: React.ReactNode;
  initialCameraData?: UnityCameraData[] | null;
  onUnityClick?: (detail: UnityClickDetail) => void;
}

export type AreaItem = Exclude<V2ElectricMapDataAreaPostResponse['list'], undefined>[number];
export type StationItem = Exclude<V2ElectricMapDataAreaV2PostResponse['list'], undefined>[number];
type WaterCardItem = Exclude<V2WaterMapDataCardDataPostResponse['list'], undefined>[number];
type FlowmeterItem = Exclude<V2WaterMapDataFlowmeterDataPostResponse['list'], undefined>[number];

const WATER_STATION_PARENT = 'Energy';
const WATER_STATION_LABEL_PARENT = 'CustomLabel';
const WATER_STATION_LABEL_PREFAB = 'Prefabs/purpleLabelRoot';
const FLOWMETER_PARENT = 'watermeter';
const FLOWMETER_LABEL_PARENT = 'WaterMeterLabel';
const FLOWMETER_PREFAB = 'Prefabs/watermeter';
const FLOWMETER_LABEL_PREFAB = 'Prefabs/WaterMeterLabelRoot';
const FLOWMETER_DEFAULT_STATUS = '开';
const FLOWMETER_TYPE = '水表';

const WATER_STATIONS = [
  {
    name: '中水站房',
    energyMediumId: 2005,
    pointLng: '121.63099747263925',
    pointLat: '31.336514049301414',
  },
  {
    name: '自来水站房',
    // 接口中的“新水”即页面展示的自来水。
    energyMediumId: 2001,
    pointLng: '121.63063794671348',
    pointLat: '31.33712167910292',
  },
] as const;

const createStationLabelText = (name: string, amount?: number) => {
  const formattedAmount = formatThousandAndUnit(amount);
  return `<align=center><color=#52E7FF>${name}</color></align>\n<align=center><color=#D8D8FF>本月用水量：</color><color=#FFFFFF>${formattedAmount.value}${formattedAmount.unit}m³</color></align>`;
};

const createWaterStationPayload = (
  cardList: WaterCardItem[],
  videoElement?: RenderStreamingVideoElement,
  includeAdd = true
) => {
  const instances = includeAdd
    ? WATER_STATIONS.flatMap(station => {
        const position = getUnityPosition(station, videoElement, '[UnityWaterMap]');
        if (!position) {
          console.warn('[UnityWaterMap] skip water station label without unity position', station);
          return [];
        }

        const waterData = cardList.find(item => item.energyMediumId === station.energyMediumId);
        return [
          {
            name: station.name,
            position,
            rotation: [0, 0, 0] as [number, number, number],
            grounded: false,
            parent: WATER_STATION_PARENT,
            properties: {
              name: station.name,
              energyMediumId: station.energyMediumId,
              monthTotal: waterData?.monthTotal,
              labelType: 'waterStation',
              key1: createStationLabelText(station.name, waterData?.monthTotal),
            },
          },
        ];
      })
    : [];

  return {
    type: 'refreshModel' as const,
    data: {
      remove: [
        {
          path: 'Prefabs/Empty',
          type: 'prefab' as const,
          instances: WATER_STATIONS.map(station => ({ name: station.name, parent: WATER_STATION_PARENT })),
        },
        {
          path: WATER_STATION_LABEL_PREFAB,
          type: 'prefab' as const,
          instances: WATER_STATIONS.map(station => ({ name: station.name, parent: WATER_STATION_LABEL_PARENT })),
        },
      ],
      add:
        instances.length > 0
          ? [
              {
                path: 'Prefabs/Empty',
                type: 'prefab' as const,
                instances,
                label: {
                  path: WATER_STATION_LABEL_PREFAB,
                  height: 70,
                  parent: WATER_STATION_LABEL_PARENT,
                },
              },
            ]
          : [],
    },
  };
};

const getFlowmeterInstanceName = (flowmeter: FlowmeterItem) =>
  String(flowmeter.flowmeterId ?? flowmeter.flowmeterName ?? 'watermeter');

const createFlowmeterLabelText = (flowmeter: FlowmeterItem) => {
  const dayTotal = formatThousandAndUnit(flowmeter.dayTotal);
  const monthTotal = formatThousandAndUnit(flowmeter.monthTotal);
  return `<align=center><color=#52E7FF>${
    flowmeter.flowmeterName ?? '流量计'
  }</color></align>\n<align=center><color=#D8D8FF>今日用水量：</color><color=#FFFFFF>${dayTotal.value}${
    dayTotal.unit
  }m³</color></align>\n<align=center><color=#D8D8FF>本月用水量：</color><color=#FFFFFF>${monthTotal.value}${
    monthTotal.unit
  }m³</color></align>`;
};

const createFlowmeterPayload = (
  flowmeterList: FlowmeterItem[],
  videoElement?: RenderStreamingVideoElement,
  includeAdd = true,
  removeList: FlowmeterItem[] = flowmeterList
) => {
  const instances = includeAdd
    ? flowmeterList
        .map(flowmeter => {
          const position = getUnityPosition(
            { ...flowmeter, pointLng: flowmeter.longitude, pointLat: flowmeter.latitude },
            videoElement,
            '[UnityWaterMap]'
          );
          if (!position) {
            console.warn('[UnityWaterMap] skip flowmeter without unity position', flowmeter);
            return null;
          }

          const name = getFlowmeterInstanceName(flowmeter);
          return {
            name,
            position,
            rotation: [0, 0, 0] as [number, number, number],
            grounded: true,
            parent: FLOWMETER_PARENT,
            properties: {
              ...flowmeter,
              name,
              labelType: 'watermeter',
              // WaterMeterLabelLODItem 根据这两个字段设置状态和类型图标；接口未提供状态时使用场景已配置的“开”。
              waterMeterCurrentStatus: FLOWMETER_DEFAULT_STATUS,
              waterMeterType: FLOWMETER_TYPE,
              waterMeterMsgs: [],
              key1: createFlowmeterLabelText(flowmeter),
            },
          };
        })
        .filter(notEmpty)
    : [];

  return {
    type: 'refreshModel' as const,
    data: {
      remove: [
        {
          path: FLOWMETER_PREFAB,
          type: 'prefab' as const,
          instances: removeList.map(flowmeter => ({
            name: getFlowmeterInstanceName(flowmeter),
            parent: FLOWMETER_PARENT,
          })),
        },
        {
          path: FLOWMETER_LABEL_PREFAB,
          type: 'prefab' as const,
          instances: removeList.map(flowmeter => ({
            name: getFlowmeterInstanceName(flowmeter),
            parent: FLOWMETER_LABEL_PARENT,
          })),
        },
      ],
      add:
        instances.length > 0
          ? [
              {
                path: FLOWMETER_PREFAB,
                type: 'prefab' as const,
                instances,
                label: {
                  path: FLOWMETER_LABEL_PREFAB,
                  height: 90,
                  parent: FLOWMETER_LABEL_PARENT,
                },
              },
            ]
          : [],
    },
  };
};

const UnityMap: FC<MapViewProps> = props => {
  const { onUnityClick, initialCameraData: _initialCameraData, ...restProps } = props;
  const { unityReady, callUnity, videoElement, registerUnityClickHandler } = useSharedUnityMap();
  const [cardList, setCardList] = useState<WaterCardItem[]>([]);
  const [flowmeterList, setFlowmeterList] = useState<FlowmeterItem[]>([]);
  const lastSyncSignatureRef = useRef('');
  const lastFlowmeterSyncSignatureRef = useRef('');
  const syncedFlowmeterListRef = useRef<FlowmeterItem[]>([]);

  const handleUnityClick = useCallback((detail: UnityClickDetail) => onUnityClick?.(detail), [onUnityClick]);

  useEffect(() => registerUnityClickHandler(handleUnityClick), [handleUnityClick, registerUnityClickHandler]);

  useEffect(() => {
    let canceled = false;
    apiV2WaterMapDataCardDataPost({}).then(res => {
      if (!canceled) setCardList(res.list ?? []);
    });
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    let canceled = false;
    apiV2WaterMapDataFlowmeterDataPost({}).then(res => {
      if (!canceled) setFlowmeterList(res.list ?? []);
    });
    return () => {
      canceled = true;
    };
  }, []);

  const syncWaterStationLabels = useCallback(async () => {
    if (!unityReady || !callUnity || !videoElement) return;

    const payload = createWaterStationPayload(cardList, videoElement);
    const signature = JSON.stringify(payload.data);
    if (lastSyncSignatureRef.current === signature) return;

    await callUnity(payload);
    lastSyncSignatureRef.current = signature;
    setUnityBusinessCleanupPayloads('waterStation', [createWaterStationPayload(cardList, undefined, false)]);
  }, [callUnity, cardList, unityReady, videoElement]);

  useEffect(() => {
    syncWaterStationLabels().catch(error => {
      console.error('[UnityWaterMap] water station label sync failed', error);
      clearUnityBusinessCleanupPayloads('waterStation');
    });
  }, [syncWaterStationLabels]);

  const syncFlowmeters = useCallback(async () => {
    if (!unityReady || !callUnity || !videoElement) return;

    const removeList = syncedFlowmeterListRef.current.length ? syncedFlowmeterListRef.current : flowmeterList;
    const payload = createFlowmeterPayload(flowmeterList, videoElement, true, removeList);
    const signature = JSON.stringify(payload.data);
    if (lastFlowmeterSyncSignatureRef.current === signature) return;

    await callUnity(payload);
    lastFlowmeterSyncSignatureRef.current = signature;
    syncedFlowmeterListRef.current = flowmeterList;
    if (flowmeterList.length) {
      setUnityBusinessCleanupPayloads('waterFlowmeter', [createFlowmeterPayload(flowmeterList, undefined, false)]);
    } else {
      clearUnityBusinessCleanupPayloads('waterFlowmeter');
    }
  }, [callUnity, flowmeterList, unityReady, videoElement]);

  useEffect(() => {
    syncFlowmeters().catch(error => {
      console.error('[UnityWaterMap] flowmeter sync failed', error);
      clearUnityBusinessCleanupPayloads('waterFlowmeter');
    });
  }, [syncFlowmeters]);

  useEffect(() => {
    const handleWaterMeterLabelClick = (event: Event) => {
      const eventDetail = (event as CustomEvent).detail;
      const detail = eventDetail?.data ? eventDetail : { data: eventDetail };
      handleUnityClick(detail);
    };

    const unityVideo = videoElement;
    unityVideo?.addEventListener('unity_waterMeterLabelClick', handleWaterMeterLabelClick as EventListener);
    unityVideo?.addEventListener('unity-waterMeterLabelClick', handleWaterMeterLabelClick as EventListener);
    window.addEventListener('waterMeterLabelClick', handleWaterMeterLabelClick as EventListener);
    return () => {
      unityVideo?.removeEventListener('unity_waterMeterLabelClick', handleWaterMeterLabelClick as EventListener);
      unityVideo?.removeEventListener('unity-waterMeterLabelClick', handleWaterMeterLabelClick as EventListener);
      window.removeEventListener('waterMeterLabelClick', handleWaterMeterLabelClick as EventListener);
    };
  }, [handleUnityClick, videoElement]);

  return <UnityOverlay {...restProps} />;
};

export default UnityMap;
